import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { PART_KIND_LABELS, type TenderOutlineNode } from '../../types/tender';

interface TreeNode extends TenderOutlineNode {
  children: TreeNode[];
}

const buildTree = (nodes: TenderOutlineNode[]): TreeNode[] => {
  const byNodeId = new Map<string, TreeNode>();
  nodes.forEach((node) => {
    byNodeId.set(node.node_id, { ...node, children: [] });
  });
  const roots: TreeNode[] = [];
  byNodeId.forEach((node) => {
    const parent = node.parent_node_id ? byNodeId.get(node.parent_node_id) : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortRecursive = (list: TreeNode[]) => {
    list.sort((a, b) => a.sort_order - b.sort_order);
    list.forEach((item) => sortRecursive(item.children));
  };
  sortRecursive(roots);
  return roots;
};

interface TenderOutlinePanelProps {
  nodes: TenderOutlineNode[];
  /** stable_id → 正文（含本地未保存内容） */
  contents: Record<string, string>;
  selectedStableId: string | null;
  onSelect: (node: TenderOutlineNode) => void;
}

/** 招标文件章节树：点击选择章节进行编制 */
export default function TenderOutlinePanel({
  nodes,
  contents,
  selectedStableId,
  onSelect,
}: TenderOutlinePanelProps) {
  const tree = buildTree(nodes);

  const renderNode = (node: TreeNode, depth: number) => {
    const hasContent = !!(contents[node.stable_id] || '').trim();
    const active = node.stable_id === selectedStableId;
    return (
      <div key={node.stable_id}>
        <button
          type="button"
          onClick={() => onSelect(node)}
          style={{ paddingLeft: `${8 + depth * 14}px` }}
          className={`flex w-full items-start gap-1.5 rounded-lg py-1.5 pr-2 text-left text-xs transition ${
            active ? 'bg-indigo-50 text-indigo-800' : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          <span className="mt-0.5 shrink-0 text-[11px] text-gray-400">{node.node_id}</span>
          <span className="min-w-0 flex-1">
            <span className="block leading-snug">{node.title}</span>
            {depth === 0 && (
              <span className="mt-0.5 block text-[11px] text-gray-400">
                {PART_KIND_LABELS[node.part_kind] || '其他'}
                {node.word_count_range ? ` · ${node.word_count_range} 字` : ''}
              </span>
            )}
          </span>
          {hasContent && (
            <CheckCircleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
          )}
        </button>
        {node.children.map((child) => renderNode(child, depth + 1))}
      </div>
    );
  };

  if (tree.length === 0) {
    return <p className="px-2 py-4 text-xs text-gray-400">暂无目录，请重新创建项目或检查模板</p>;
  }

  return <div className="space-y-0.5">{tree.map((node) => renderNode(node, 0))}</div>;
}
